import { observable, action, computed, reaction } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { message } from 'antd';
import { uniq } from 'lodash';
import { ProjectsRootVisualStore } from '../../common/stores';
import { ProjectApplicationDefinitionEditVisualStore } from '../../iota_applications/stores';
import { ConditionModel, InputGroupModel } from '../models';
import { InputMeta } from '../../iota_applications/types';
import { ApplicationDefinitionConditional } from '../../iota_applications/types/ApplicationDefinition';
import {
    AdditionalClassificationDto as AdditionalClassificationDtoBase,
    AdditionalClassificationOptionDto,
    CreateEditConditionFormValues,
    CreateEditInputGroupFormValues,
    LayoutStructureDto
} from '../types';
import ApplicationDefinitionConditionalService from '../services/ApplicationDefinitionConditionalService';
import { RcFile } from 'antd/lib/upload';
import { ImportInputsResponse } from '../../common/types/types';
import { endpoints } from '../../../config';

interface ConditionFormValues extends CreateEditConditionFormValues {}

interface InputGroupFormValues extends CreateEditInputGroupFormValues {}

interface AdditionalClassification extends AdditionalClassificationDtoBase {}

export default class ApplicationDefinitionConditionalEditStore {
    @observable
    activeTab: string = 'conditions';

    @observable
    createEditConditionDialogVisible: boolean = false;

    @observable
    createEditInputGroupDialogVisible: boolean = false;

    @observable
    conditions: ConditionModel[] = [];

    @observable
    inputGroups: InputGroupModel[] = [];

    @observable
    isUpdating: boolean = false;

    @observable
    isLoading: boolean = false;

    @observable
    currentImportFile: RcFile | undefined = undefined;

    @observable
    isImportConfirmDialogVisible: boolean = false;

    @observable
    isImportingConditions: boolean = false;

    @observable
    isDataChanged: boolean = false;

    @observable
    addAdditionalClassificationModalVisible: boolean = false;

    @observable
    isLoadingAdditionalClassifications: boolean = false;

    @observable
    additionalClassificationOptions: AdditionalClassificationOptionDto[] = [];

    @observable
    additionalClassifications: AdditionalClassification[] = [];

    @observable
    selectedAdditionalClassification: AdditionalClassificationOptionDto | undefined = undefined;

    @observable
    previewAdditionalClassification: AdditionalClassification | undefined = undefined;

    @observable
    isLoadingAdditionalClassificationPreview: boolean = false;

    @observable
    additionalClassificationsError: string | undefined = undefined;

    @observable
    additionalClassificationsPreviewError: string | undefined = undefined;

    @observable
    additionalClassificationModalStep: 'select' | 'preview' = 'select';

    @observable
    canEditAdditionalClassifications: boolean = false;

    @computed
    get project() {
        return this.projectsRootStore.currentProject;
    }

    @computed
    get applicationDefinition() {
        const applicationDefinition = this.applicationDefinitionEditVisualStore.currentAppDef;

        return applicationDefinition instanceof ApplicationDefinitionConditional ? applicationDefinition : null;
    }

    @computed
    get applicationDefinitionInputs(): InputMeta[] {
        if (!this.applicationDefinition) {
            return [];
        }

        return JSON.parse(this.applicationDefinition.meta).inputs;
    }

    @computed
    get applicationDefinitionLayoutStructure(): LayoutStructureDto | null {
        if (!this.applicationDefinition) {
            return null;
        }

        return JSON.parse(this.applicationDefinition.meta).layoutStructure;
    }

    @computed
    get applicationDefinitionSectionNames() {
        return uniq(this.applicationDefinitionInputs.filter(i => !!i.section).map(i => i.section!));
    }

    @computed
    get selectedCondition() {
        return this.conditions.find(c => c.selected);
    }

    @computed
    get editableCondition() {
        return this.conditions.find(c => c.editable);
    }

    @computed
    get editableInputGroup() {
        return this.inputGroups.find(g => g.editable);
    }

    @computed
    get importBindingsActionUrl() {
        if (!this.project || !this.applicationDefinition) {
            return '';
        }

        return (
            endpoints.manageUrl +
            `projects/${this.project.id}/iota/applications/${this.applicationDefinition.id}/import-conditions`
        );
    }

    @computed
    get fileImportActionHeaders() {
        return this.applicationDefinitionEditVisualStore.fileImportActionHeaders;
    }

    constructor(
        private readonly projectsRootStore: ProjectsRootVisualStore,
        private readonly applicationDefinitionEditVisualStore: ProjectApplicationDefinitionEditVisualStore,
        private readonly service: ApplicationDefinitionConditionalService
    ) {
        reaction(
            () => this.applicationDefinition,
            applicationDefinition => {
                if (applicationDefinition) {
                    this.setActiveTab('conditions');
                    this.clearEditableData();
                    this.setEditableData(applicationDefinition);
                }
            }
        );
    }

    @action.bound
    setActiveTab(activeTab: string) {
        this.activeTab = activeTab;
    }

    @action.bound
    setCreateEditConditionDialogVisible(createEditConditionDialogVisible: boolean) {
        this.createEditConditionDialogVisible = createEditConditionDialogVisible;
    }

    @action.bound
    setCreateEditInputGroupDialogVisible(createEditInputGroupDialogVisible: boolean) {
        this.createEditInputGroupDialogVisible = createEditInputGroupDialogVisible;
    }

    @action.bound
    async openAddAdditionalClassificationModal() {
        if (!this.canEditAdditionalClassifications) {
            return;
        }

        this.addAdditionalClassificationModalVisible = true;
        this.selectedAdditionalClassification = undefined;
        await this.loadAdditionalClassificationOptions();
    }

    @action.bound
    closeAddAdditionalClassificationModal() {
        this.addAdditionalClassificationModalVisible = false;
        this.selectedAdditionalClassification = undefined;
        this.previewAdditionalClassification = undefined;
        this.additionalClassificationsError = undefined;
        this.additionalClassificationsPreviewError = undefined;
        this.additionalClassificationModalStep = 'select';
    }

    @action.bound
    setSelectedAdditionalClassification(option: AdditionalClassificationOptionDto | undefined) {
        this.selectedAdditionalClassification = option;
    }

    @action.bound
    async loadAdditionalClassificationOptions() {
        if (!this.project || !this.applicationDefinition) {
            return;
        }

        this.isLoadingAdditionalClassifications = true;
        this.additionalClassificationsError = undefined;

        try {
            const resp = await this.service.getAdditionalClassifications(this.project.id, this.applicationDefinition.id);

            if (resp.isOk()) {
                this.additionalClassificationOptions = this.filterConfiguredAdditionalClassificationOptions(resp.value);
                this.refreshAdditionalClassificationAvailability(resp.value);
            } else {
                this.additionalClassificationOptions = [];
                this.additionalClassificationsError = 'Failed to load additional classifications';
                message.error('Failed to load additional classifications');
            }
        } finally {
            this.isLoadingAdditionalClassifications = false;
        }
    }

    @action.bound
    refreshAdditionalClassificationAvailability(availableOptions: AdditionalClassificationOptionDto[] = this.additionalClassificationOptions) {
        const availableTypeCodes = new Set(availableOptions.map(option => option.typeCode));

        this.additionalClassifications = this.additionalClassifications.map(classification => {
            if (availableTypeCodes.has(classification.typeCode)) {
                return {
                    ...classification,
                    isAvailable: true,
                    unavailableReason: undefined
                };
            }

            return {
                ...classification,
                isAvailable: false,
                unavailableReason: 'This classification is not available in the current SICS environment.'
            };
        });
    }

    @action.bound
    async handleAdditionalClassificationNext() {
        if (
            !this.canEditAdditionalClassifications ||
            !this.project ||
            !this.applicationDefinition ||
            !this.selectedAdditionalClassification
        ) {
            return;
        }

        this.isLoadingAdditionalClassificationPreview = true;
        this.additionalClassificationsPreviewError = undefined;

        try {
            const resp = await this.service.getAdditionalClassificationPreview(
                this.project.id,
                this.applicationDefinition.id,
                this.selectedAdditionalClassification.typeCode
            );

            if (resp.isOk()) {
                this.previewAdditionalClassification = resp.value;
                this.additionalClassificationModalStep = 'preview';
            } else {
                this.additionalClassificationsPreviewError = 'Failed to load classification dependencies';
                message.error('Failed to load classification dependencies');
            }
        } finally {
            this.isLoadingAdditionalClassificationPreview = false;
        }
    }

    @action.bound
    handleAdditionalClassificationBack() {
        this.previewAdditionalClassification = undefined;
        this.additionalClassificationsPreviewError = undefined;
        this.additionalClassificationModalStep = 'select';
    }

    @action.bound
    confirmAdditionalClassification() {
        if (!this.canEditAdditionalClassifications || !this.previewAdditionalClassification) {
            return;
        }

        this.additionalClassifications = [
            ...this.additionalClassifications.filter(
                classification => classification.typeCode !== this.previewAdditionalClassification!.typeCode
            ),
            this.previewAdditionalClassification
        ];
        this.syncAdditionalClassificationInputs();
        this.setIsDataChanged(true);
        this.closeAddAdditionalClassificationModal();
    }

    @action.bound
    removeAdditionalClassification(classification: AdditionalClassification) {
        if (!this.canEditAdditionalClassifications) {
            return;
        }

        this.additionalClassifications = this.additionalClassifications.filter(
            item => item.typeCode !== classification.typeCode
        );
        this.syncAdditionalClassificationInputs();
        this.setIsDataChanged(true);
    }

    @action.bound
    moveAdditionalClassification(classification: AdditionalClassification, direction: -1 | 1) {
        if (!this.canEditAdditionalClassifications) {
            return;
        }

        const currentIndex = this.additionalClassifications.findIndex(item => item.typeCode === classification.typeCode);

        this.moveAdditionalClassificationToIndex(currentIndex, currentIndex + direction);
    }

    @action.bound
    moveAdditionalClassificationToIndex(currentIndex: number, nextIndex: number) {
        if (!this.canEditAdditionalClassifications) {
            return;
        }

        if (
            currentIndex === nextIndex ||
            currentIndex < 0 ||
            nextIndex < 0 ||
            currentIndex >= this.additionalClassifications.length ||
            nextIndex >= this.additionalClassifications.length
        ) {
            return;
        }

        const nextClassifications = [...this.additionalClassifications];
        const [classification] = nextClassifications.splice(currentIndex, 1);

        if (!classification) {
            return;
        }

        nextClassifications.splice(nextIndex, 0, classification);
        this.additionalClassifications = nextClassifications;
        this.syncAdditionalClassificationInputs();
        this.setIsDataChanged(true);
    }

    @action.bound
    addNewCondition(formValues: ConditionFormValues) {
        const condition = new ConditionModel(this, {
            ...formValues,
            conditionId: uuidv4(),
            name: formValues.name.trim(),
            priority: this.conditions.length + 1
        });

        this.conditions.push(condition);
    }

    @action.bound
    addNewInputGroup(formValues: InputGroupFormValues) {
        const inputGroup = new InputGroupModel(this, {
            ...formValues,
            inputGroupId: uuidv4(),
            name: formValues.name.trim(),
            enabled: true,
            isDefault: false,
            bindingGroups: []
        });

        this.inputGroups.push(inputGroup);

        return inputGroup;
    }

    @action.bound
    removeCondition(condition: ConditionModel) {
        this.conditions.splice(this.conditions.indexOf(condition), 1);
    }

    @action.bound
    removeInputGroup(inputGroup: InputGroupModel) {
        this.inputGroups.splice(this.inputGroups.indexOf(inputGroup), 1);
    }

    @action.bound
    clearEditableData() {
        this.conditions = [];
        this.inputGroups = [];
        this.additionalClassifications = [];
        this.syncAdditionalClassificationInputs();
    }

    @action.bound
    setEditableData(applicationDefinition: ApplicationDefinitionConditional) {
        this.conditions = applicationDefinition.conditions.map(condition => new ConditionModel(this, condition));
        this.additionalClassifications = applicationDefinition.additionalClassifications ?? [];
        this.syncAdditionalClassificationInputs();

        if (applicationDefinition.inputGroups.length) {
            this.inputGroups = applicationDefinition.inputGroups.map(group => new InputGroupModel(this, group));
        } else {
            this.createDefaultInputGroups();
        }
    }

    @action.bound
    createDefaultInputGroups() {
        if (!this.applicationDefinitionLayoutStructure) {
            return;
        }

        this.inputGroups = this.applicationDefinitionLayoutStructure.defaultLayoutInputGroups.map(
            group =>
                new InputGroupModel(this, {
                    inputGroupId: uuidv4(),
                    name: group.name,
                    enabled: true,
                    isDefault: group.isDefault,
                    layoutSettingsId: group.layoutSettingsId,
                    bindingGroups: []
                })
        );
    }

    @action.bound
    async updateData() {
        try {
            if (!this.project || !this.applicationDefinition) {
                return;
            }

            this.isUpdating = true;

            const resp = await this.service.updateData(this.project.id, this.applicationDefinition.id, {
                conditions: this.conditions.map(c => c.dto),
                inputGroups: this.inputGroups.map(i => i.dto),
                additionalClassifications: this.additionalClassifications,
                fieldSettings: this.applicationDefinitionEditVisualStore.getFieldSettingsPayload()
            });

            if (resp.isOk()) {
                const updatedApp = resp.value;
                this.applicationDefinition.setLastUpdatedInfo(updatedApp.lastUpdatedBy, updatedApp.lastUpdatedTime);
                this.setIsDataChanged(false);
                message.success('Application has been successfully updated');
            } else {
                message.error('Failed to update application');
            }
        } finally {
            this.isUpdating = false;
        }
    }

    @action.bound
    exportAppBindings() {
        if (!this.applicationDefinition || !this.project) {
            console.error('No current app def or project');
            return;
        }

        this.service.exportAppConditions(this.applicationDefinition.id, this.project.id);
    }

    @action.bound
    getUserName(userId: string) {
        return this.applicationDefinitionEditVisualStore.getUserNameById(userId);
    }

    @action.bound
    setHeaders() {
        return this.applicationDefinitionEditVisualStore.setHeaders();
    }

    @action.bound
    setCurrentImportFile(file: RcFile | undefined) {
        this.currentImportFile = file;
    }

    @action.bound
    handleImportResponse(response: ImportInputsResponse) {
        if (response.importedSuccessfully) {
            const appDefId = this.applicationDefinition!.id;
            this.applicationDefinitionEditVisualStore.setCurrentAppDef(undefined);

            // Needed for correct UI update
            setTimeout(() => {
                message.success('You have successfully updated the application Inputs bindings.');
                this.setCurrentImportFile(undefined);
                this.applicationDefinitionEditVisualStore.loadApplication(appDefId);
            }, 0);
            return;
        }

        if (!response.canProceed) {
            message.error(response.responseMessage);
            return;
        }

        if (response.canProceed) {
            this.setIsImportConfirmDialogVisible(true);
        }
    }

    @action.bound
    setIsImportingConditions(isImporting: boolean) {
        this.isImportingConditions = isImporting;
    }

    @action.bound
    setIsImportConfirmDialogVisible(visible: boolean) {
        this.isImportConfirmDialogVisible = visible;
    }

    @action.bound
    handleImportDialogCancel() {
        this.setIsImportConfirmDialogVisible(false);
        this.setCurrentImportFile(undefined);
    }

    @action.bound
    async handleImportDialogConfirm() {
        if (!this.applicationDefinition || !this.project || !this.currentImportFile) {
            return;
        }

        try {
            this.setIsImportingConditions(true);
            const resp = await this.service.commitAppBindings(
                this.applicationDefinition!.id,
                this.project.id,
                this.currentImportFile!
            );

            if (resp.isOk()) {
                const appDefId = this.applicationDefinition!.id;
                this.applicationDefinitionEditVisualStore.setCurrentAppDef(undefined);

                // Needed for correct UI update
                setTimeout(() => {
                    this.applicationDefinitionEditVisualStore.loadApplication(appDefId);
                    this.handleImportDialogCancel();
                    message.success('You have successfully updated the application Inputs bindings.');
                }, 0);
            } else {
                message.error(resp.error.data);
                console.error(resp.error.data);
            }
        } catch (err) {
            message.error(err);
            console.error(err);
        } finally {
            this.setIsImportingConditions(false);
        }
    }

    @action.bound
    setIsDataChanged(isChanged: boolean) {
        this.isDataChanged = isChanged;
    }

    @action.bound
    setCanEditAdditionalClassifications(canEditAdditionalClassifications: boolean) {
        this.canEditAdditionalClassifications = canEditAdditionalClassifications;
    }

    navigateToList() {
        if (this.project) {
            this.projectsRootStore.navigateToApplicationDefinitionsPage(this.project);
        }
    }

    @action.bound
    private syncAdditionalClassificationInputs() {
        this.applicationDefinitionEditVisualStore.syncAdditionalClassificationInputs(this.additionalClassifications);
        this.rebuildInputGroups();
    }

    @action.bound
    private rebuildInputGroups() {
        this.inputGroups = this.inputGroups.map(inputGroup => new InputGroupModel(this, inputGroup.dto));
    }

    private filterConfiguredAdditionalClassificationOptions(
        options: AdditionalClassificationOptionDto[]
    ): AdditionalClassificationOptionDto[] {
        const configuredClassifications = this.flattenAdditionalClassifications(this.additionalClassifications);
        const configuredTypeCodes = new Set(configuredClassifications.map(classification => classification.typeCode));
        const configuredSlots = new Set(configuredClassifications.map(classification => classification.classificationSlot));

        return options.filter(
            option => !configuredTypeCodes.has(option.typeCode) && !configuredSlots.has(option.classificationSlot)
        );
    }

    private flattenAdditionalClassifications(
        classifications: AdditionalClassificationDtoBase[]
    ): AdditionalClassificationDtoBase[] {
        return classifications.reduce<AdditionalClassificationDtoBase[]>(
            (acc, classification) => [
                ...acc,
                classification,
                ...this.flattenAdditionalClassifications(classification.children ?? [])
            ],
            []
        );
    }
}
